import toast from 'react-hot-toast'

import { useMutation, useQueryClient } from '@fv/client-core'
import type { RouteGuideExecution } from '@fv/models'

import { quoteRequestKeys } from '@/features/quote-request/useQuoteRequest'
import { loadKeys } from '@/hooks/shipments/useLoad'
import { quoteKeys } from '@/hooks/shipments/useQuotes'
import { shipperFetch } from '@/utils/shipperFetch'

import { routeGuideKeys } from './queries'

function useInvalidateRouteGuide(loadId: string) {
  const queryClient = useQueryClient()

  return () => {
    // Spot quoting unlocks off the load's status, not the execution.
    queryClient.invalidateQueries({
      queryKey: routeGuideKeys.execution(loadId),
    })
    queryClient.invalidateQueries({ queryKey: loadKeys.id(loadId) })
    queryClient.invalidateQueries({ queryKey: quoteRequestKeys.all })
    // Ending the waterfall retracts the contracted quotes; match every quote version for the load.
    queryClient.invalidateQueries({
      queryKey: [...quoteKeys.all, loadId],
    })
  }
}

export function useSkipToSpot(loadId: string) {
  const invalidate = useInvalidateRouteGuide(loadId)

  return useMutation(
    () =>
      shipperFetch<RouteGuideExecution>(
        `/route-guides/executions/${loadId}/skip`,
        { method: 'POST' },
      ),
    {
      onSuccess: () => {
        invalidate()
        toast.success('Route guide skipped, continue with spot quotes')
      },
      onError: (e: Error) => toast.error(e.message),
    },
  )
}

export function useBookTpeRate(loadId: string) {
  const invalidate = useInvalidateRouteGuide(loadId)

  return useMutation(
    () =>
      shipperFetch<RouteGuideExecution>(
        `/route-guides/executions/${loadId}/book-tpe`,
        { method: 'POST' },
      ),
    {
      onSuccess: () => {
        invalidate()
        toast.success('Route guide ended, booking with CHR')
      },
      onError: (e: Error) => {
        invalidate()
        toast.error(e.message)
      },
    },
  )
}
